// An Excel-style column filter (the Order sheet's): the column's values, each with a tick box and how many
// vessels have it, "(Blanks)" and "+" first, plus one-tap "Only blanks", "Only +" and "Has a value".
// A tick applies at once. Desktop: a popover under whatever opened it (the column's header, or the
// toolbar's "Filter a column"). Touch: a bottom sheet.
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useIsCoarse } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { BLANK, type ValueCount } from '../features/orderSheetFilter.js';
import { Modal } from './ui.js';

const POP_W = 270;
const POP_MAX_H = 400;

export function ColumnFilter({ label, anchor, values, keep, onChange, onClose, name }: {
  label: string;
  /** what opened it; the popover sits under it. */
  anchor: HTMLElement;
  values: readonly ValueCount[];
  /** the values ticked, or null when the column isn't filtered (every value shows). */
  keep: readonly string[] | null;
  /** the new ticks, or null to stop filtering the column. */
  onChange: (keep: string[] | null) => void;
  onClose: () => void;
  /** how a value reads ('' is the blank one). */
  name: (value: string) => string;
}) {
  const coarse = useIsCoarse();
  const pop = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const all = values.map((x) => x.value);
  const ticked = (v: string) => keep == null || keep.includes(v);
  const needle = fold(q.trim());
  const shown = needle ? values.filter((x) => hasText(needle, name(x.value))) : values;
  const nonBlank = all.filter((v) => v !== BLANK);
  const allTicked = all.every(ticked);
  const noneTicked = !all.some(ticked);
  const only = (vs: string[]) => onChange(vs);
  const toggle = (v: string) => {
    const cur = keep ?? all;
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  const close = () => { onClose(); if (anchor.isConnected) anchor.focus(); };

  // desktop: under the anchor (above it when there's no room), inside the window; it follows the anchor
  // as the sheet scrolls under it, and closes once the anchor has gone out of view
  useLayoutEffect(() => {
    if (coarse) return;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      if (!anchor.isConnected || r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) { onClose(); return; }
      const h = Math.min(POP_MAX_H, pop.current?.offsetHeight ?? POP_MAX_H);
      const top = window.innerHeight - r.bottom >= h + 8 ? r.bottom + 4 : Math.max(8, r.top - 4 - h);
      setPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - POP_W - 8)) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [anchor, coarse, onClose, values.length, keep]);
  // a click anywhere else closes it (the anchor toggles it itself)
  useEffect(() => {
    if (coarse) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !anchor.contains(t)) onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [anchor, coarse, onClose]);
  // keyboard: start on the search (a long list) or the first tick box
  useEffect(() => {
    if (coarse) return;
    pop.current?.querySelector<HTMLElement>('.cfilt-q, .cfilt-list input')?.focus();
  }, [coarse]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };

  const body = (
    <>
      <div className="cfilt-quick">
        {all.includes(BLANK) && <button type="button" className="btn btn-sm" onClick={() => only([BLANK])}>Only blanks</button>}
        {all.includes('+') && <button type="button" className="btn btn-sm" onClick={() => only(['+'])}>Only +</button>}
        {all.includes(BLANK) && nonBlank.length > 0 && <button type="button" className="btn btn-sm" onClick={() => only(nonBlank)}>Has a value</button>}
      </div>
      {values.length > 8 && (
        <input className="field cfilt-q" value={q} placeholder="Search values" aria-label={`Search the values of ${label}`} onChange={(e) => setQ(e.currentTarget.value)} />
      )}
      <ul className="cfilt-list" role="group" aria-label={`${label} values`}>
        {!needle && (
          <li>
            <label className="cfilt-opt cfilt-all">
              <input
                type="checkbox"
                checked={allTicked}
                ref={(el) => { if (el) el.indeterminate = !allTicked && !noneTicked; }}
                onChange={() => onChange(allTicked ? [] : null)}
              />
              <span>Select all</span>
            </label>
          </li>
        )}
        {shown.map((x) => (
          <li key={x.value}>
            <label className={`cfilt-opt${x.value === BLANK ? ' blank' : ''}`}>
              <input type="checkbox" checked={ticked(x.value)} onChange={() => toggle(x.value)} />
              <span className="cfilt-v">{name(x.value)}</span>
              <span className="cfilt-n">{x.count}</span>
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="cfilt-none">No match</li>}
      </ul>
    </>
  );
  const foot = (
    <>
      <button type="button" className="btn btn-sm" disabled={keep == null} onClick={() => onChange(null)}>Clear filter</button>
      <button type="button" className="btn btn-sm btn-primary" onClick={close}>Done</button>
    </>
  );

  if (coarse) {
    // portalled: a header inside the sticky, scrolling sheet would trap the sheet's fixed layer
    return createPortal(
      <Modal sheet title={`Filter · ${label}`} onClose={close} footer={foot}>
        <div className="cfilt-sheet" onKeyDown={onKey}>{body}</div>
      </Modal>,
      document.body,
    );
  }
  return createPortal(
    <div
      ref={pop}
      className="cfilt-pop"
      role="dialog"
      aria-label={`Filter ${label}`}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: POP_W, maxHeight: POP_MAX_H }}
      onKeyDown={onKey}
    >
      <div className="cfilt-hd">Filter · {label}</div>
      {body}
      <div className="cfilt-foot">{foot}</div>
    </div>,
    document.body,
  );
}
